/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 16 2024
 **/

import axios from "axios";

import type { MessageList, TranslateResult, chatOption } from "./types";

export const translateMessage = async (message: string, translate: chatOption["translate"]) => {
	const parsedMessage = JSON.parse(message) as ChatServices.Format2.Message;

	let plainMessage = "";
	parsedMessage.elements.forEach((element) => {
		if (element.type === "section") {
			element.elements.forEach((content) => {
				if (content.type === "text") {
					plainMessage += `${content.text}`;
				}
			});
		} else if (element.type === "text") {
			plainMessage += `${element.text}`;
		}
	});

	const response = await axios.post<{ result: TranslateResult[] }>("https://ai.rsup.io/api/language/translate", {
		message: plainMessage.split("\n"),
		language: translate
	});

	const result = {
		type: "richtext",
		elements: []
	} as ChatServices.Format2.Container.Richtext;

	response.data.result.forEach((v) => {
		result.elements.push({
			type: "section",
			elements: [{ text: v.translatedText, type: "text" }]
		});
	});

	return result;
};

export const updateMessageWithReactions = (
	prev: MessageList[string],
	payload: ChatServices.Server.MessageReacted.Data
): MessageList[string] => {
	try {
		const messageToUpdate = prev;

		const reactionIndex = messageToUpdate.reactions.findIndex(
			(reaction) => reaction.type === payload.reaction.type
		);

		const hasReactionAlready = reactionIndex >= 0;

		const getUpdatedReaction = () => {
			const updatedReaction = { ...messageToUpdate.reactions[reactionIndex] };

			const addReactedUser = () => {
				updatedReaction.count += 1;
				updatedReaction.users.push({
					id: payload.reaction.member.id,
					name: payload.reaction.member.name,
					colorProfile: payload.reaction.member.colorProfile
				});
				return updatedReaction;
			};

			const removeReactedUser = () => {
				updatedReaction.count -= 1;
				updatedReaction.users = updatedReaction.users.filter((user) => user.id !== payload.reaction.member.id);
				return updatedReaction;
			};

			return payload.reacted ? addReactedUser() : removeReactedUser();
		};

		const newFirstReaction = {
			count: 1,
			users: [
				{
					id: payload.reaction.member.id,
					name: payload.reaction.member.name,
					colorProfile: payload.reaction.member.colorProfile
				}
			],
			type: payload.reaction.type
		};

		if (hasReactionAlready) {
			messageToUpdate.reactions[reactionIndex] = getUpdatedReaction();
		} else if (payload.reacted) {
			messageToUpdate.reactions.push(newFirstReaction);
		}

		messageToUpdate.reactions = messageToUpdate.reactions.filter((reaction) => reaction.count > 0);

		return {
			...prev,
			[payload.reaction.messageId]: messageToUpdate
		};
	} catch {
		return prev;
	}
};
